import { Seo } from '@/components/Seo';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, Scale, AlertTriangle, CreditCard, Ban, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function AGBPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Seo
        title="Allgemeine Geschäftsbedingungen (AGB)"
        description="Allgemeine Geschäftsbedingungen für die Nutzung der marKIpilot-Plattform der MYSYMP AG: Leistungen, Abonnements, Zahlungsbedingungen und Haftung."
        path="/agb"
      />
      {/* Navigation - with iPhone Safe Area */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/5" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2">
              <Logo size="small" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content - with safe area padding */}
      <div style={{ paddingTop: 'calc(env(safe-area-inset-top) + 5rem)', paddingBottom: 'max(4rem, env(safe-area-inset-bottom))' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">
              Allgemeine Geschäftsbedingungen
            </h1>
            <p className="text-slate-400 mb-8">Gültig ab: Februar 2026</p>

            <div className="space-y-8">
              {/* Scope */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-teal-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">1. Geltungsbereich</h2>
                </div>
                <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
                  <p>
                    Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Dienstleistungen, die von marKIpilot, einem Service der MYSYMP AG, erbracht werden.
                  </p>
                  <p>
                    Mit der Nutzung unserer Dienste oder der Beauftragung von Leistungen akzeptieren Sie diese AGB. Abweichende Bedingungen des Kunden gelten nur, wenn diese von uns schriftlich anerkannt wurden.
                  </p>
                </div>
              </section>

              {/* Services */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Scale className="w-5 h-5 text-purple-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">2. Leistungen</h2>
                </div>
                <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
                  <p>
                    <strong className="text-white">Pay-per-Install (PPI) Modell:</strong> Unsere Hauptdienstleistung basiert auf dem Pay-per-Install Modell. Der Kunde zahlt ausschliesslich für verifizierte App-Installationen.
                  </p>
                  <p>
                    <strong className="text-white">Verifikation:</strong> Als verifizierte Installationen gelten Downloads, die über anerkannte Mobile Measurement Partner (MMP) wie AppsFlyer oder Adjust nachgewiesen werden.
                  </p>
                  <p>
                    <strong className="text-white">Zusätzliche Dienste:</strong> Creative-Erstellung, ASO-Optimierung, Tracking-Setup und Beratungsleistungen werden separat vereinbart und abgerechnet.
                  </p>
                </div>
              </section>

              {/* Pricing */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-green-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">3. Preise und Zahlung</h2>
                </div>
                <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
                  <p>
                    Die Preise für unsere Dienstleistungen werden individuell vereinbart und im jeweiligen Angebot festgehalten. Alle Preise verstehen sich in CHF oder EUR, zuzüglich der gesetzlichen Mehrwertsteuer.
                  </p>
                  <p>
                    <strong className="text-white">Zahlungsbedingungen:</strong> Rechnungen sind innerhalb von 30 Tagen nach Rechnungsstellung ohne Abzug zahlbar, sofern nicht anders vereinbart.
                  </p>
                  <p>
                    <strong className="text-white">Verzug:</strong> Bei Zahlungsverzug sind wir berechtigt, Verzugszinsen von 5% p.a. zu berechnen.
                  </p>
                </div>
              </section>

              {/* Liability */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">4. Haftung</h2>
                </div>
                <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
                  <p>
                    Wir haften nicht für indirekte Schäden, entgangenen Gewinn oder Datenverlust. Unsere Haftung ist auf die Höhe der vom Kunden für die jeweilige Dienstleistung bezahlten Vergütung begrenzt.
                  </p>
                  <p>
                    Wir übernehmen keine Garantie für bestimmte Ergebnisse im Bereich App-Marketing, da diese von zahlreichen externen Faktoren abhängen.
                  </p>
                </div>
              </section>

              {/* Termination */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <Ban className="w-5 h-5 text-red-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">5. Kündigung</h2>
                </div>
                <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
                  <p>
                    Die Zusammenarbeit kann von beiden Seiten mit einer Frist von 30 Tagen zum Monatsende gekündigt werden, sofern nicht anders vereinbart.
                  </p>
                  <p>
                    Bei wichtigem Grund (z.B. Zahlungsverzug, Verstoss gegen diese AGB) ist eine fristlose Kündigung möglich.
                  </p>
                </div>
              </section>

              {/* Changes */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 text-blue-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">6. Änderungen der AGB</h2>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Wir behalten uns vor, diese AGB jederzeit zu ändern. Änderungen werden den Kunden mindestens 30 Tage vor Inkrafttreten mitgeteilt. Bei Widerspruch steht dem Kunden ein ausserordentliches Kündigungsrecht zu.
                </p>
              </section>

              {/* Jurisdiction */}
              <section className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl border border-white/10 p-6 sm:p-8">
                <h2 className="font-display text-xl font-bold mb-4">7. Anwendbares Recht und Gerichtsstand</h2>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Es gilt ausschliesslich schweizerisches Recht. Gerichtsstand ist Luzern, Schweiz. Wir behalten uns jedoch vor, den Kunden an seinem Wohnsitz zu verklagen.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-xs">© {new Date().getFullYear()} marKIpilot – MYSYMP AG</span>
            <div className="flex items-center gap-4">
              <Link to="/impressum" className="text-slate-500 hover:text-slate-300 text-xs">Impressum</Link>
              <Link to="/datenschutz" className="text-slate-500 hover:text-slate-300 text-xs">Datenschutz</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
